import React, { useEffect, useId, useRef, useState } from 'react';
import './AppSelect.css';
import './AppMultiSelect.css';

export default function AppMultiSelect({
  values = [],
  onChange,
  options = [],
  placeholder = 'Select',
  disabled = false,
  className = '',
  id,
  minWidth,
  checkboxPosition = 'left',
  'aria-label': ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const fallbackId = useId();
  const selectId = id || fallbackId;
  const pointerHandledRef = useRef(false);

  const selectedValues = values.map(String);
  const selectedCount = selectedValues.length;
  const selectedLabels = selectedValues.map(
    (value) => options.find((opt) => String(opt.value) === value)?.label ?? value
  );
  const displayLabel = selectedCount === 0 ? placeholder : selectedLabels.join(', ');
  const checkboxOnRight = checkboxPosition === 'right';

  useEffect(() => {
    if (!open) return undefined;

    const onDocPointerDown = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      setOpen(false);
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    const timer = window.setTimeout(() => {
      document.addEventListener('pointerdown', onDocPointerDown, true);
    }, 0);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggleOpen = () => {
    if (disabled) return;
    setOpen((current) => !current);
  };

  const runPointerAction = (event, action) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    // The click that follows this press must not run the action again.
    // A loading render can delay that click past any short timer, which was
    // toggling the serial number back off.
    pointerHandledRef.current = true;
    action();
  };

  const runClickFallback = (event, action) => {
    event.stopPropagation();
    if (event.detail !== 0 && pointerHandledRef.current) {
      pointerHandledRef.current = false;
      return;
    }
    pointerHandledRef.current = false;
    action();
  };

  const toggleValue = (optValue) => {
    const key = String(optValue);
    const next = selectedValues.includes(key)
      ? values.filter((v) => String(v) !== key)
      : [...values, optValue];
    onChange?.(next);
  };

  return (
    <div
      ref={rootRef}
      className={[
        'app-select',
        'app-multi-select',
        checkboxOnRight ? 'app-multi-select--checkbox-right' : '',
        open ? 'is-open' : '',
        disabled ? 'is-disabled' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={minWidth ? { minWidth } : undefined}
    >
      <button
        type="button"
        id={selectId}
        className="app-select__trigger"
        onPointerDown={(event) => runPointerAction(event, toggleOpen)}
        onClick={(event) => runClickFallback(event, toggleOpen)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className={`app-select__value${selectedCount ? '' : ' is-placeholder'}`}>
          {displayLabel}
        </span>
        <i className="ti ti-chevron-down app-select__chevron" aria-hidden="true" />
      </button>
      {open && (
        <ul
          className="app-select__menu"
          role="listbox"
          aria-multiselectable="true"
          aria-labelledby={selectId}
          onPointerDown={(event) => event.stopPropagation()}
        >
          {options.length === 0 ? (
            <li className="app-multi-select__empty" role="presentation">
              No options available
            </li>
          ) : (
            options.map((opt) => {
              const isSelected = selectedValues.includes(String(opt.value));
              return (
                <li key={String(opt.value)} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`app-select__option app-multi-select__option${isSelected ? ' is-selected' : ''}${checkboxOnRight ? ' is-checkbox-right' : ''}`}
                    onPointerDown={(event) => runPointerAction(event, () => toggleValue(opt.value))}
                    onClick={(event) => runClickFallback(event, () => toggleValue(opt.value))}
                  >
                    {!checkboxOnRight && (
                      <span className="app-multi-select__checkbox" aria-hidden="true">
                        {isSelected && <i className="ti ti-check" />}
                      </span>
                    )}
                    <span className="app-multi-select__label">{opt.label}</span>
                    {checkboxOnRight && (
                      <span className="app-multi-select__checkbox" aria-hidden="true">
                        {isSelected && <i className="ti ti-check" />}
                      </span>
                    )}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
